import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toLocalIsoString } from "@/lib/agendaConstants";
import { AGENDA } from "@/constants/testIds";

/** Crea un bloqueo de horario (vacaciones, ausencia) para un profesional. */
export default function BlockModal({ open, onOpenChange, professionals, initial, onSaved }) {
  const [professionalId, setProfessionalId] = useState("");
  const [startValue, setStartValue] = useState("");
  const [endValue, setEndValue] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setProfessionalId(initial?.professionalId || professionals[0]?.id || "");
    const base = initial?.date ? new Date(initial.date) : new Date();
    base.setMinutes(0, 0, 0);
    const end = new Date(base.getTime() + 60 * 60000);
    setStartValue(toLocalIsoString(base).slice(0, 16));
    setEndValue(toLocalIsoString(end).slice(0, 16));
    setReason("");
  }, [open, initial, professionals]);

  const handleSave = async () => {
    setError("");
    if (!professionalId) return setError("Elegi un profesional.");
    if (!startValue || !endValue) return setError("Elegi fecha/hora de inicio y fin.");
    setSaving(true);
    try {
      await api.post("/schedule-blocks", {
        professional_id: professionalId,
        start_time: `${startValue}:00`,
        end_time: `${endValue}:00`,
        reason: reason || null,
      });
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={AGENDA.blockModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Bloquear horario</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Profesional</Label>
            <Select value={professionalId} onValueChange={setProfessionalId}>
              <SelectTrigger data-testid={AGENDA.blockProfessionalSelect}><SelectValue placeholder="Elegi un profesional" /></SelectTrigger>
              <SelectContent>
                {professionals.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Desde</Label>
              <Input type="datetime-local" value={startValue} onChange={(e) => setStartValue(e.target.value)} data-testid={AGENDA.blockStartInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Hasta</Label>
              <Input type="datetime-local" value={endValue} onChange={(e) => setEndValue(e.target.value)} data-testid={AGENDA.blockEndInput} />
            </div>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Motivo (opcional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej: Vacaciones, ausencia médica" data-testid={AGENDA.blockReasonInput} />
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={AGENDA.blockCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={AGENDA.blockSaveButton}>
            {saving ? "Guardando..." : "Bloquear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
